import { useEffect, useMemo, useState } from 'react';

import CircleAddedIcon from '@/assets/all-icons/circle-added-icon';
import CircleNotAddedIcon from '@/assets/all-icons/circle-not-added-icon';
import PrivacyPoliciesIcon from '@/assets/all-icons/privacy-policies-icon';
import config from '@/config';
import useTranslations from '@/i18n/useTranslations';
import UserService from '@/services/user.service';
import XIcon from '@/shared-components/icons/components/public/XIcon';
import Loader from '@/shared-components/loader/Loader';
import GenericPopup from '@/shared-components/popups/components/GenericPopup';
import { getStickerId, getStickerImageId } from '@/shared-components/stickers/utils';
import Text from '@/shared-components/text/components/Text';

import ProfileBadgeClaimExperience from './ProfileBadgeClaimExperience';
import {
  normaliseNameKey,
  PROFILE_BADGES,
  PROFILE_BADGES_BY_NAME,
} from './profileBadgesConfig';

import '../profile-top-buttons/components/styles/NotificationModal.css';
import '../profile-top-buttons/components/styles/ProfileOverlayPopup.css';
import './styles/ProfileBadgesModal.css';

const BADGE_STATE_STORAGE_KEY = 'profileBadgeStates';
const DEFAULT_BADGE_STATE = {};
const PROFILE_BADGES_VIEW = 'badges';
const PROFILE_STICKERS_VIEW = 'stickers';

const extractStickers = (payload) => {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.data)) return payload.data;
  if (Array.isArray(payload?.results)) return payload.results;
  if (Array.isArray(payload?.stickers)) return payload.stickers;
  if (Array.isArray(payload?.items)) return payload.items;
  return [];
};

const deriveBackendBadgeState = (badge) => {
  if (typeof badge.badgeVisibility === 'boolean') {
    return badge.badgeVisibility ? 'claimed' : (badge.obtainedAt || badge.badgeList?.length ? 'claimable' : 'locked');
  }

  if (typeof badge.isClaimed === 'boolean') {
    return badge.isClaimed ? 'claimed' : (badge.obtainedAt ? 'claimable' : 'locked');
  }

  const requirements = Array.isArray(badge.badgeList)
    ? badge.badgeList
    : Array.isArray(badge.badge_list)
      ? badge.badge_list
      : [];

  if (requirements.length === 0) {
    return 'locked';
  }

  const allCompleted = requirements.every((requirement) => Boolean(requirement?.requirement_completed));
  return allCompleted ? 'claimable' : 'locked';
};

const normalizeServerBadge = (badge) => {
  const fallbackRequirements = badge.obtainedAt
    ? [
      {
        requirement_key: 'badge_obtained',
        requirement_label: 'Badge obtained',
        requirement_completed: true,
        requirement_completed_at: badge.obtainedAt,
      }
    ]
    : [];

  const requirements = Array.isArray(badge.badge_list)
    ? badge.badge_list
    : Array.isArray(badge.badgeList)
      ? badge.badgeList
      : fallbackRequirements;
  const completed = requirements.filter((requirement) => Boolean(requirement?.requirement_completed)).length;
  const requirementDescription = requirements.length
    ? `${completed}/${requirements.length} requirement${requirements.length === 1 ? '' : 's'} completed`
    : '';

  const rawName = badge.badge ?? badge.badge_name ?? badge.name;
  const trimmedName = typeof rawName === 'string' ? rawName.trim() : '';
  const canonicalName = trimmedName || null;
  const metadataKey = normaliseNameKey(canonicalName);
  const metaByName = metadataKey ? PROFILE_BADGES_BY_NAME[metadataKey] : null;
  const identifier = badge._id ?? badge.id ?? metaByName?.id ?? `${canonicalName ?? 'badge'}_${badge._id ?? badge.id ?? 'unknown'}`;

  return {
    id: identifier,
    name: canonicalName ?? metaByName?.name ?? 'Badge',
    badgeName: canonicalName ?? metaByName?.name ?? null,
    description: (badge.description ?? requirementDescription) || metaByName?.description || '',
    image: badge.image ?? metaByName?.image ?? null,
    rewardTitle: badge.rewardTitle ?? metaByName?.rewardTitle,
    badgeList: requirements,
    badgeVisibility: typeof badge.badge_visibility === 'boolean'
      ? badge.badge_visibility
      : typeof badge.badgeVisibility === 'boolean'
        ? badge.badgeVisibility
        : Boolean(badge.isClaimed),
    badgeClaimDate: badge.badge_claim_date ?? badge.badgeClaimDate ?? badge.obtainedAt ?? null,
    obtainedAt: badge.obtainedAt ?? null,
    isClaimed: typeof badge.isClaimed === 'boolean' ? badge.isClaimed : Boolean(badge.badge_visibility || badge.badgeVisibility),
  };
};

export default function ProfileBadgesModal({
  open,
  onClose,
  username,
  selectedBadgeId,
  onSelectBadge,
  isOwner,
  userBadges = [],
  badgesLoading = false,
  badgesError = null,
  onClaimBadge,
}) {
  const t = useTranslations('common.profile.badges');
  const [badgeStates, setBadgeStates] = useState(() => {
    if (typeof window === 'undefined') {
      return DEFAULT_BADGE_STATE;
    }

    try {
      const stored = window.localStorage.getItem(BADGE_STATE_STORAGE_KEY);
      if (!stored) {
        return DEFAULT_BADGE_STATE;
      }
      const parsed = JSON.parse(stored);
      return {
        ...DEFAULT_BADGE_STATE,
        ...(parsed && typeof parsed === 'object' ? parsed : {}),
      };
    } catch (error) {
      console.error('Failed to parse badge states from storage', error);
      return DEFAULT_BADGE_STATE;
    }
  });
  const [activeClaimBadgeId, setActiveClaimBadgeId] = useState(null);
  const [claimError, setClaimError] = useState(null);
  const [activeView, setActiveView] = useState(PROFILE_STICKERS_VIEW);
  const [stickers, setStickers] = useState([]);
  const [stickersLoading, setStickersLoading] = useState(false);
  const [stickersError, setStickersError] = useState(null);

  const serverBadgesByName = useMemo(() => {
    if (!Array.isArray(userBadges) || userBadges.length === 0) {
      return new Map();
    }

    const map = new Map();
    userBadges.forEach((badge) => {
      const normalized = normalizeServerBadge(badge);
      const key = normaliseNameKey(normalized.badgeName ?? normalized.name);
      if (key && !map.has(key)) {
        map.set(key, normalized);
      }
    });
    return map;
  }, [userBadges]);

  const displayBadges = useMemo(() => {
    if (!PROFILE_BADGES?.length) {
      return Array.from(serverBadgesByName.values());
    }

    const usedKeys = new Set();
    const ordered = PROFILE_BADGES.map((template) => {
      const key = normaliseNameKey(template.name);
      if (key && serverBadgesByName.has(key)) {
        const normalized = serverBadgesByName.get(key);
        usedKeys.add(key);
        return {
          ...normalized,
          id: template.id,
          name: template.name,
          badgeName: template.name,
          description: normalized.description || template.description,
          image: normalized.image || template.image,
          rewardTitle: normalized.rewardTitle || template.rewardTitle,
        };
      }

      return {
        id: template.id,
        name: template.name,
        badgeName: template.name,
        description: template.description,
        image: template.image,
        rewardTitle: template.rewardTitle,
        badgeList: [],
        badgeVisibility: false,
        badgeClaimDate: null,
      };
    });

    serverBadgesByName.forEach((badge, key) => {
      if (!usedKeys.has(key)) {
        ordered.push(badge);
      }
    });

    return ordered;
  }, [serverBadgesByName]);

  useEffect(() => {
    if (!displayBadges.length) {
      return undefined;
    }

    setBadgeStates((prev) => {
      let next = prev;
      displayBadges.forEach((badge) => {
        const backendState = deriveBackendBadgeState(badge);
        if (next[badge.id] !== backendState) {
          if (next === prev) {
            next = { ...prev };
          }
          next[badge.id] = backendState;
        }
      });
      return next;
    });
  }, [displayBadges]);

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    try {
      window.localStorage.setItem(BADGE_STATE_STORAGE_KEY, JSON.stringify(badgeStates));
    } catch (error) {
      console.error('Failed to persist badge states', error);
    }
    return undefined;
  }, [badgeStates]);

  useEffect(() => {
    if (!open) {
      setClaimError(null);
    }
  }, [open]);

  useEffect(() => {
    if (open) {
      setActiveView(PROFILE_STICKERS_VIEW);
      return;
    }

    setActiveClaimBadgeId(null);
    setClaimError(null);
    setStickersError(null);
  }, [open]);

  const closeModal = () => {
    if (typeof onClose === 'function') {
      onClose();
    }
  };

  const normalizedUsername = useMemo(
    () => (typeof username === 'string' ? username.replace(/^@+/, '').trim() : ''),
    [username]
  );

  const formattedUsername = normalizedUsername || t('fallback.user');

  useEffect(() => {
    let cancelled = false;

    if (!open) {
      setStickers([]);
      setStickersLoading(false);
      setStickersError(null);
      return () => {
        cancelled = true;
      };
    }

    if (!isOwner && !normalizedUsername) {
      setStickers([]);
      setStickersLoading(false);
      setStickersError(null);
      return () => {
        cancelled = true;
      };
    }

    setStickersLoading(true);
    setStickersError(null);

    const request = isOwner
      ? UserService.getAllUserStickers('me')
      : UserService.getUserStickers(normalizedUsername);

    request
      .then(({ response, data }) => {
        if (cancelled) return;
        if (!response?.ok) {
          throw new Error(t('errors.load_failed'));
        }

        const parsed = extractStickers(data)
          .map((sticker, index) => {
            const imageId = getStickerImageId(sticker);
            const resolvedId = getStickerId(sticker) || `${imageId || 'sticker'}_${index}`;
            if (!imageId) return null;
            return {
              id: String(resolvedId),
              imageId,
              cropShape: sticker?.cropShape || sticker?.crop_shape || 'rect',
            };
          })
          .filter(Boolean);

        setStickers(parsed);
      })
      .catch((error) => {
        if (cancelled) return;
        setStickers([]);
        setStickersError(error?.message || t('errors.load_failed'));
      })
      .finally(() => {
        if (cancelled) return;
        setStickersLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [isOwner, normalizedUsername, open, t]);

  const getResolvedBadgeState = (badge) => {
    const backendState = deriveBackendBadgeState(badge);
    if (!isOwner) {
      return backendState;
    }
    return badgeStates[badge.id] ?? backendState;
  };

  const hasClaimedBadge = useMemo(
    () => displayBadges.some((badge) => getResolvedBadgeState(badge) === 'claimed'),
    [displayBadges, badgeStates, isOwner]
  );

  const activeClaimBadge = useMemo(
    () => displayBadges.find((badge) => badge.id === activeClaimBadgeId) || null,
    [activeClaimBadgeId, displayBadges]
  );

  const badgesCount = displayBadges.length;
  const stickersCount = stickers.length;
  const canRenderClearSelectionAction = isOwner && !activeClaimBadge && (hasClaimedBadge || Boolean(selectedBadgeId));

  const updateBadgeState = (badgeId, state) => {
    setBadgeStates((prev) => ({
      ...prev,
      [badgeId]: state,
    }));
  };

  const handleBadgeKeyDown = (event, badgeId, isDisabled) => {
    if (isDisabled) return;
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      onSelectBadge?.(badgeId);
    }
  };

  const handleClaimBadge = (badgeId) => {
    setClaimError(null);
    setActiveClaimBadgeId(badgeId);
  };
  const handleClaimComplete = async (claimedBadge) => {
    const badgeId = claimedBadge?.id ?? claimedBadge;
    const badgeName = claimedBadge?.badgeName ?? claimedBadge?.name ?? null;

    if (!badgeId) {
      return;
    }

    if (typeof onClaimBadge === 'function' && badgeName) {
      try {
        await onClaimBadge(badgeName);
      } catch (error) {
        setClaimError(error?.message || t('errors.claim_failed'));
        return;
      }
    }

    updateBadgeState(badgeId, 'claimed');
    setClaimError(null);
  };
  const handleClaimExperienceClose = () => setActiveClaimBadgeId(null);

  return (
    <GenericPopup
      className="profile-overlay-popup notifications-popup"
      open={open}
      opacity
    >
      <div className={`notifications-content__container profile-badges-modal__container ${!isOwner ? 'profile-badges-modal__container--auto' : ''}`}>
        <div className="generic-popup__header">
          <Text
            className="profile-badges-modal__title generic-popup__header-text-theme font-size-large"
            paragraph>
            {isOwner ? t('title.owner') : t('title.visitor', { username: formattedUsername })}
          </Text>
          <XIcon className="generic-popup__close" onClick={closeModal} />
        </div>

        <div className="user-profile__community-navigation element-non-selectable">
          <div
            className={`user-profile__community-navigation-link ${activeView === PROFILE_STICKERS_VIEW ? 'active' : ''}`}
            onClick={() => setActiveView(PROFILE_STICKERS_VIEW)}
          >
            <span className="user-profile__community-navigation-link-text">
              {`Stickers (${stickersCount})`}
            </span>
          </div>
          <div
            className={`user-profile__community-navigation-link ${activeView === PROFILE_BADGES_VIEW ? 'active' : ''}`}
            onClick={() => setActiveView(PROFILE_BADGES_VIEW)}
          >
            <span className="user-profile__community-navigation-link-text">
              {`Badges (${badgesCount})`}
            </span>
          </div>
        </div>

        <div className="profile-badges-modal__list" role="list">
          {activeView === PROFILE_BADGES_VIEW && (
            <>
              {badgesLoading ? (
                <div className="profile-badges-modal__loading-state">
                  <Loader inline thin />
                </div>
              ) : (
                <>
                  {badgesError && (
                    <Text
                      className="profile-badges-modal__status profile-badges-modal__status--error font-size-small"
                      paragraph>
                      {badgesError.message || t('errors.load_failed')}
                    </Text>
                  )}
                  {claimError && (
                    <Text
                      className="profile-badges-modal__status profile-badges-modal__status--error font-size-small"
                      paragraph>
                      {claimError}
                    </Text>
                  )}

                  {displayBadges.map((badge) => {
                    const isSelected = badge.id === selectedBadgeId;
                    const StatusIcon = isSelected ? CircleAddedIcon : CircleNotAddedIcon;
                    const badgeState = getResolvedBadgeState(badge);
                    const isLocked = badgeState === 'locked';
                    const isClaimable = badgeState === 'claimable';
                    const isClaimed = badgeState === 'claimed';
                    const isDisabled = isLocked || isClaimable;

                    return (
                      <div
                        key={badge.id}
                        className={`profile-badges-modal__item ${isSelected ? 'selected' : ''} ${isLocked ? 'is-locked' : ''} ${isClaimable ? 'is-claimable' : ''} ${isClaimed ? 'is-claimed' : ''}`}
                        role="button"
                        tabIndex={isDisabled ? -1 : 0}
                        aria-disabled={isDisabled}
                        onClick={() => {
                          if (isDisabled) return;
                          onSelectBadge?.(badge.id);
                        }}
                        onKeyDown={(event) => handleBadgeKeyDown(event, badge.id, isDisabled)}
                      >
                        {badge.image ? (
                          <img src={badge.image} alt={badge.name} className="profile-badges-modal__badge-image" />
                        ) : (
                          <div className="profile-badges-modal__badge-image profile-badges-modal__badge-image--empty" aria-hidden />
                        )}
                        <div className="profile-badges-modal__badge-copy">
                          <Text className="profile-badges-modal__badge-name">
                            {badge.name}
                          </Text>
                          <Text className="profile-badges-modal__badge-description">
                            {badge.description}
                          </Text>
                        </div>
                        {isOwner && <StatusIcon className="profile-badges-modal__badge-and-sticker-status" />}

                        {isOwner && (isLocked || isClaimable) && (
                          <div
                            className={`profile-badges-modal__item-overlay ${isClaimable ? 'profile-badges-modal__item-overlay--claimable' : ''} ${isLocked ? 'profile-badges-modal__item-overlay--locked' : ''}`}
                            onClick={(event) => event.stopPropagation()}
                            role="presentation"
                          >
                            {isLocked ? (
                              <div className="profile-badges-modal__item-overlay-copy">
                                <div className="profile-badges-modal__locked-indicator">
                                  <PrivacyPoliciesIcon className="profile-badges-modal__locked-icon" />
                                  <span className="profile-badges-modal__locked-label">{t('labels.locked_badge')}</span>
                                </div>
                              </div>
                            ) : (
                              <button
                                type="button"
                                className="profile-badges-modal__claim-button"
                                onClick={(event) => {
                                  event.stopPropagation();
                                  handleClaimBadge(badge.id);
                                }}
                              >
                                {t('actions.claim_badge')}
                              </button>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </>
              )}
            </>
          )}

          {activeView === PROFILE_STICKERS_VIEW && (
            <>
              {stickersLoading ? (
                <div className="profile-badges-modal__loading-state">
                  <Loader inline thin />
                </div>
              ) : (
                <>
                  {stickersError && (
                    <Text
                      className="profile-badges-modal__status profile-badges-modal__status--error font-size-small"
                      paragraph>
                      {stickersError}
                    </Text>
                  )}
                  {!stickersError && stickersCount === 0 && (
                    <Text className="profile-badges-modal__status font-size-small" paragraph>
                      No stickers from channels yet.
                    </Text>
                  )}
                  {!stickersError && stickersCount > 0 && (
                    <div className="profile-badges-modal__stickers-grid remove-scrollbar">
                      {stickers.map((sticker) => {
                        const isSelected = sticker.id === selectedBadgeId;
                        const StatusIcon = isSelected ? CircleAddedIcon : CircleNotAddedIcon;

                        return (
                          <div
                            key={sticker.id}
                            className={`profile-badges-modal__sticker-card ${sticker.cropShape === 'circle' ? 'profile-badges-modal__sticker-card--circle' : ''} ${isSelected ? 'selected' : ''}`}
                            role="button"
                            tabIndex={0}
                            onClick={() => onSelectBadge?.(sticker.id)}
                            onKeyDown={(event) => {
                              if (event.key === 'Enter' || event.key === ' ') {
                                event.preventDefault();
                                onSelectBadge?.(sticker.id);
                              }
                            }}
                          >
                            <img
                              src={`${config.app.imageServiceDomain}/${sticker.imageId}/w=256`}
                              alt="Sticker"
                              className={`profile-badges-modal__sticker-image ${sticker.cropShape === 'circle' ? 'profile-badges-modal__sticker-image--circle' : ''}`}
                              loading="lazy"
                            />
                            {isOwner && (
                              <StatusIcon className="profile-badges-modal__badge-and-sticker-status profile-badges-modal__badge-and-sticker-status--sticker" />
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </>
              )}
            </>
          )}
        </div>

        {canRenderClearSelectionAction && (
          <div className="profile-badges-modal__footer">
            <div className="profile-badges-modal__footer-actions profile-badges-modal__footer-actions--single">
              <button
                type="button"
                className="profile-badges-modal__clear"
                onClick={() => onSelectBadge?.(null)}
              >
                {t('footer.clear_selection')}
              </button>
            </div>
          </div>
        )}
      </div>
      {activeClaimBadge && (
        <ProfileBadgeClaimExperience
          badge={activeClaimBadge}
          onClose={handleClaimExperienceClose}
          onClaimed={handleClaimComplete}
        />
      )}
    </GenericPopup>
  );
}
